import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import HomeView from '@/views/HomeView.vue';
import ContactView from '@/views/ContactView.vue';
import axios from 'axios';
import { Constant } from '@/config/constants';
import DemoAdminView from '@/views/DemoAdminView.vue';
import PaymentSuccessView from '@/views/PaymentSuccessView.vue';

// Define custom meta fields for routes
interface CustomRouteMeta {
  title?: string;
  description?: string;
  robots?: string;
  canonicalPath?: string;
  requiresAuth?: boolean;
}

// Augment the vue-router module to properly type meta fields
declare module 'vue-router' {
  interface RouteMeta extends CustomRouteMeta {}
}

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      title: 'DuckNSeek | Cruise Duck QR Tags and Sticker Packs',
      description: 'Create and track cruise duck adventures with QR tags, sticker packs, and easy setup from DuckNSeek.',
      canonicalPath: '/',
      robots: 'index, follow',
      requiresAuth: false
    }
  },
  // Login route removed since we're using a popup dialog now
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/AdminView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Admin Dashboard',
      robots: 'noindex, nofollow',
    }
  },
  {
    path: '/duck/:id',
    name: 'duck',
    component: () => import('@/views/DuckView.vue'),
    meta: {
      title: 'Duck Discovery',
      description: 'Track a discovered cruise duck and see its message history on DuckNSeek.',
      robots: 'noindex, follow',
    }
  },
  {
    path: '/confirm',
    name: 'ConfirmView',
    component: () => import("@/views/ConfirmView.vue"),
    meta: {
      title: 'Confirm Email',
      robots: 'noindex, nofollow',
    }
  },
  {
    path: '/waiting-confirmation/:email',
    name: 'WaitingConfirmation',
    component: () => import("@/views/WaitingConfirmationView.vue"),
    meta: {
      title: 'Waiting for Confirmation',
      robots: 'noindex, nofollow',
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: {
      title: 'Reset Password',
      robots: 'noindex, nofollow',
    }
  },
  {
    path: '/duck-found/:id',
    name: 'DuckFound',
    component: () => import('@/views/DuckFoundView.vue'),
    meta: {
      title: 'Duck Found',
      robots: 'noindex, follow',
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: ContactView,
    meta: {
      title: 'Contact DuckNSeek',
      description: 'Get in touch with DuckNSeek for questions about cruise ducks, orders, and support.',
      canonicalPath: '/contact',
      robots: 'index, follow',
      requiresAuth: false
    }
  },
  {
    path: '/story',
    name: 'story',
    component: () => import('@/views/StoryView.vue'),
    meta: {
      title: 'What Is Duck Hiding on a Cruise? | DuckNSeek Story',
      description: 'Learn what duck hiding on a cruise means, where the tradition started, and how DuckNSeek makes cruise duck adventures easier to track and share.',
      canonicalPath: '/story',
      robots: 'index, follow',
      requiresAuth: false
    }
  },
  {
    path: '/demo',
    name: 'demo',
    component: DemoAdminView,
    meta: {
      title: 'DuckNSeek Demo',
      description: 'Try the DuckNSeek demo to preview how cruise duck tracking and messaging work.',
      canonicalPath: '/demo',
      robots: 'index, follow',
      requiresAuth: false
    }
  },
  {
    path: '/demo/duck/:id?', // Make the ID parameter optional
    name: 'demoDuck',
    component: () => import('@/views/DemoDuckView.vue'),
    meta: {
      title: 'Duck Discovery - Demo',
      robots: 'noindex, follow',
      requiresAuth: false
    }
  },
  {
    path: '/payment-success',
    name: 'payment-success',
    component: PaymentSuccessView,
    meta: {
      title: 'Payment Success',
      requiresAuth: true,
      robots: 'noindex, nofollow'
    }
  },
  // Add this new route to handle Stripe's redirect for success
  {
    path: '/checkout/success',
    name: 'checkout-success',
    component: PaymentSuccessView,
    meta: {
      title: 'Checkout Success',
      requiresAuth: true,
      robots: 'noindex, nofollow'
    }
  },
  // Add this new route to handle Stripe's redirect for cancellation
  {
    path: '/checkout/cancel',
    name: 'checkout-cancel',
    component: () => import('@/views/PaymentCancelView.vue'),
    meta: {
      title: 'Checkout Canceled',
      requiresAuth: true,
      robots: 'noindex, nofollow'
    }
  },
];

const SEO_DEFAULTS = {
  title: 'DuckNSeek | Cruise Duck QR Tags and Sticker Packs',
  description: 'DuckNSeek helps cruisers create and track cruise duck adventures with QR tags, messages, and sticker packs.',
  robots: 'index, follow',
  canonicalPath: '/',
  siteUrl: 'https://ducknseek.com',
};

const STORY_FAQ_JSON_LD_ID = 'ducknseek-story-faq-jsonld';

function upsertMetaTag(attribute: 'name' | 'property', key: string, content: string): void {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function upsertCanonicalLink(href: string): void {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
}

function upsertJsonLdScript(id: string, payload: Record<string, unknown>): void {
  let script = document.head.querySelector(`script#${id}`) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('id', id);
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(payload);
}

function removeJsonLdScript(id: string): void {
  const script = document.head.querySelector(`script#${id}`);
  if (script) {
    script.remove();
  }
}

function updateRouteStructuredData(to: RouteLocationNormalized, canonicalUrl: string): void {
  if (to.path === '/story') {
    upsertJsonLdScript(STORY_FAQ_JSON_LD_ID, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is duck hiding on a cruise?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Duck hiding on a cruise is a friendly activity where travelers hide rubber ducks around the ship for others to find. Ducks often include notes or QR tags so finders can share where they were discovered and continue the fun.',
          },
        },
      ],
      mainEntityOfPage: canonicalUrl,
      inLanguage: 'en',
    });
    return;
  }

  removeJsonLdScript(STORY_FAQ_JSON_LD_ID);
}

function updateSeoTags(to: RouteLocationNormalized): void {
  const title = (to.meta.title as string) || SEO_DEFAULTS.title;
  const description = (to.meta.description as string) || SEO_DEFAULTS.description;
  const robots = (to.meta.robots as string) || SEO_DEFAULTS.robots;
  const canonicalPath = ((to.meta.canonicalPath as string) || to.path || SEO_DEFAULTS.canonicalPath).replace(/\/$/, '') || '/';
  const canonicalUrl = `${SEO_DEFAULTS.siteUrl}${canonicalPath === '/' ? '/' : canonicalPath}`;

  document.title = title;
  upsertMetaTag('name', 'description', description);
  upsertMetaTag('name', 'robots', robots);
  upsertMetaTag('property', 'og:title', title);
  upsertMetaTag('property', 'og:description', description);
  upsertMetaTag('property', 'og:url', canonicalUrl);
  upsertMetaTag('name', 'twitter:title', title);
  upsertMetaTag('name', 'twitter:description', description);
  upsertMetaTag('name', 'twitter:url', canonicalUrl);
  upsertCanonicalLink(canonicalUrl);
  updateRouteStructuredData(to, canonicalUrl);
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Ensure navigation scrolls to top by default and supports hash anchors
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    return { left: 0, top: 0, behavior: 'smooth' };
  },
});

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();

  updateSeoTags(to);

  // Special handling for pricing routes - always allow access
  if (to.path === '/pricing' || to.hash === '#pricing') {
    next();
    return;
  }

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated()) {
      // Redirect to home with login=true query parameter instead of login page
      next({ 
        path: '/',
        query: { 
          showLogin: 'true',
          redirect: to.fullPath,
          message: 'Please log in to access this page'
        }
      });
      return;
    }
    
    try {
      // For authenticated routes, verify token
      // but skip verification for pricing-related paths
      if (!to.path.includes('/pricing') && !to.hash.includes('pricing')) {
        await axios.get(`${Constant.API_URL}/auth/my-cruises`);
      }
      next();
    } catch (error) {
      // If token is invalid or no cruises, redirect to home with login dialog
      authStore.logout();
      next({ 
        path: '/',
        query: { 
          showLogin: 'true',
          redirect: to.fullPath,
          message: 'Your session has expired. Please log in again.'
        }
      });
    }
  } else {
    next();
  }
});

export default router;